import type { ParentType } from '@risksmart-app/domain/src/types/consts/index';
import type { GetFormCustomisationResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetFormCustomisationQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetFormCustomisationDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

import { mapFormCustomisationTrpcToGraphQL } from './mapFormCustomisationTrpcToGraphQL';

type UseGetFormCustomisationByParentTypesArgs = {
  parentTypes: ParentType[];
};

/**
 * Flag-gated full-customisation read for the sectioned form editor + live
 * rendering. The tRPC response (form_configuration with nested sections,
 * field configs, and both ordering tables) is flattened by
 * `mapFormCustomisationTrpcToGraphQL` into the flat GraphQL shape so the
 * existing provider mapping stays unchanged.
 */
export const useGetFormCustomisationByParentTypes = createQueryHook<
  UseGetFormCustomisationByParentTypesArgs,
  GetFormCustomisationResponseRow[],
  GetFormCustomisationQuery
>({
  trpcQueryOptions: (trpc, { parentTypes }) =>
    trpc.frontend.formConfiguration.getFormCustomisation.queryOptions({
      parentTypes,
    }),
  // The mapping is structurally compatible at runtime — both sides hold the
  // same parent-type literal strings — but Drizzle's InferQueryModel widens
  // `FormConfigurationParentType` to `string` on the tRPC side while the
  // GraphQL codegen narrows it to `Parent_Type_Enum`. The two types are
  // nominally distinct, so route via `unknown` to satisfy the structural cast.
  mapTrpcDataToGraphQL: (data) =>
    mapFormCustomisationTrpcToGraphQL(
      data
    ) as unknown as GetFormCustomisationQuery,
  graphqlDocument: GetFormCustomisationDocument,
  graphqlVariables: ({ parentTypes }) => ({ parentTypes }),
});
